import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ShoppingBag, Trash2, CheckCircle, Sparkles, AlertTriangle, X } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface MarketplaceListing {
  id: string;
  channel_id: string;
  product_id: string;
  variant_id: string | null;
  product_name: string;
  product_slug: string;
  variant_name: string | null;
  variant_sku: string | null;
  channel_name: string;
  channel_platform: string;
  status: string;
  status_message: string | null;
  override_price: number | null;
  effective_price: number;
  available_quantity: number;
  last_synced_at: string | null;
  created_at: string;
}

interface ListingsResponse {
  listings: MarketplaceListing[];
  pagination: PaginationData;
}

interface ChannelOption {
  id: string;
  name: string;
  platform: string;
}

interface ProductSuggestion {
  product_id: string;
  product_name: string;
  score: number;
  image_count: number;
  total_inventory: number;
  base_price: number;
  breakdown: Record<string, number>;
}

interface QualityData {
  total: number;
  active: number;
  errors: number;
  suppressed: number;
  missing_images: number;
  short_descriptions: number;
  out_of_stock: number;
}

const PLATFORM_LABELS: Record<string, string> = {
  google_shopping: 'Google',
  facebook_shopping: 'Facebook',
  amazon: 'Amazon',
  ebay: 'eBay',
  etsy: 'Etsy',
  walmart: 'Walmart',
  tiktok_shop: 'TikTok',
};

const PLATFORM_COLORS: Record<string, any> = {
  google_shopping: 'blue',
  facebook_shopping: 'indigo',
  amazon: 'yellow',
  ebay: 'red',
  etsy: 'orange',
  walmart: 'blue',
  tiktok_shop: 'pink',
};

const STATUS_COLORS: Record<string, any> = {
  draft: 'gray',
  pending: 'yellow',
  active: 'green',
  inactive: 'gray',
  error: 'red',
  suppressed: 'orange',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const columns: ColumnDef<MarketplaceListing>[] = [
  {
    id: 'product',
    header: 'Product',
    sortable: true,
    cell: ({ row }) => (
      <div>
        <Link to={`/admin/products/${row.product_id}`} className="font-medium text-gray-900 hover:text-blue-600">
          {row.product_name}
        </Link>
        {row.variant_name && (
          <div className="text-sm text-gray-500">{row.variant_name} ({row.variant_sku})</div>
        )}
      </div>
    ),
  },
  {
    id: 'channel',
    header: 'Channel',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Badge variant={PLATFORM_COLORS[row.channel_platform] || 'gray'}>
          {PLATFORM_LABELS[row.channel_platform] || row.channel_platform}
        </Badge>
        <span className="text-sm text-gray-600">{row.channel_name}</span>
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => (
      <Badge variant={STATUS_COLORS[row.status] || 'gray'}>{row.status}</Badge>
    ),
  },
  {
    id: 'price',
    header: 'Price',
    align: 'right',
    cell: ({ row }) => (
      <span className="text-sm font-medium">${Number(row.effective_price).toFixed(2)}</span>
    ),
  },
  {
    id: 'inventory',
    header: 'Inventory',
    align: 'right',
    cell: ({ row }) => {
      const qty = Number(row.available_quantity) || 0;
      return (
        <span className={`text-sm ${qty <= 0 ? 'text-red-600 font-medium' : qty < 10 ? 'text-yellow-600' : 'text-gray-600'}`}>
          {qty}
        </span>
      );
    },
  },
  {
    id: 'last_synced',
    header: 'Last Synced',
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">{timeAgo(row.last_synced_at)}</span>
    ),
  },
];

const MarketplaceListings = () => {
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestChannel] = useState('');
  const [quality, setQuality] = useState<QualityData | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState } = useDataTableState({
    storageKey: 'admin-marketplace-listings',
    defaultSort: 'product',
    defaultLimit: 25,
    columns,
  });

  // Fetch channel options for filter
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const { channels: chs } = await api.get<{ channels: ChannelOption[] }>('/admin/marketplace-channels?limit=100');
        setChannels(chs);
      } catch {
        // Ignore — filter will just be empty
      }
    };
    fetchChannels();
  }, []);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (state.search) params.set('q', state.search);
        if (channelFilter) params.set('channel_id', channelFilter);
        if (statusFilter) params.set('status', statusFilter);
        const response = await api.get<ListingsResponse>(`/admin/marketplace-listings?${params.toString()}`);
        setListings(response.listings);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load listings');
      } finally {
        setIsLoading(false);
      }
    };
    fetchListings();
  }, [state.page, state.limit, state.search, state.sort, state.order, channelFilter, statusFilter]);

  // Fetch quality indicators when channel filter is set
  useEffect(() => {
    if (!channelFilter) { setQuality(null); return; }
    const fetchQuality = async () => {
      try {
        const { quality: q } = await api.get<{ quality: QualityData }>(`/admin/marketplace-listings/quality?channel_id=${channelFilter}`);
        setQuality(q);
      } catch { setQuality(null); }
    };
    fetchQuality();
  }, [channelFilter, listings]);

  const handleSmartSuggest = async () => {
    const targetChannel = suggestChannel || channelFilter || channels[0]?.id;
    if (!targetChannel) { addToast('Select a channel first', 'error'); return; }
    setIsSuggesting(true);
    try {
      const { suggestions: s } = await api.post<{ suggestions: ProductSuggestion[] }>('/admin/marketplace-listings/smart-suggest', { channel_id: targetChannel, limit: 20 });
      setSuggestions(s);
      setShowSuggest(true);
    } catch (err: any) {
      addToast(err.message || 'Failed to get suggestions', 'error');
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleListSuggested = async (productIds: string[]) => {
    const targetChannel = suggestChannel || channelFilter || channels[0]?.id;
    if (!targetChannel || productIds.length === 0) return;
    try {
      const result = await api.post<{ created: number }>('/admin/marketplace-listings/bulk', {
        channel_id: targetChannel,
        product_ids: productIds,
        status: 'active',
      });
      addToast(`Listed ${result.created} variant(s) across ${productIds.length} product(s)`, 'success');
      setShowSuggest(false);
      // Refresh listings
      updateState({ page: 1 });
    } catch (err: any) {
      addToast(err.message || 'Failed to list products', 'error');
    }
  };

  const bulkActions: BulkAction<MarketplaceListing>[] = [
    {
      id: 'activate',
      label: 'Activate',
      icon: <CheckCircle className="w-4 h-4 mr-1" />,
      variant: 'primary',
      confirmMessage: 'Activate the selected listings?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.put(`/admin/marketplace-listings/${row.id}`, { status: 'active' });
        }
        setListings((prev) => prev.map((l) =>
          rows.some((r) => r.id === l.id) ? { ...l, status: 'active' } : l
        ));
        addToast(`${rows.length} listing(s) activated`, 'success');
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4 mr-1" />,
      variant: 'danger',
      confirmMessage: 'Are you sure you want to delete the selected listings?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.delete(`/admin/marketplace-listings/${row.id}`);
        }
        setListings((prev) => prev.filter((l) => !rows.some((r) => r.id === l.id)));
        addToast(`${rows.length} listing(s) deleted`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketplace Listings</h1>
          <p className="text-gray-600 mt-1">Manage product listings across sales channels</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="md" onClick={handleSmartSuggest} isLoading={isSuggesting}>
            <Sparkles className="w-5 h-5 mr-2" />
            Smart Suggest
          </Button>
          <Link to="/admin/marketplace/channels">
            <Button variant="outline" size="md">
              <Plus className="w-5 h-5 mr-2" />
              Manage Channels
            </Button>
          </Link>
        </div>
      </div>

      {/* Quality Bar */}
      {quality && channelFilter && (
        <div className="flex items-center gap-4 bg-stone-50 border border-stone-200 rounded-lg px-4 py-2 text-sm">
          <span className="font-medium text-stone-700">Quality:</span>
          <span className="text-green-600">{quality.active} active</span>
          {quality.errors > 0 && <span className="text-red-600">{quality.errors} errors</span>}
          {quality.suppressed > 0 && <span className="text-orange-600">{quality.suppressed} suppressed</span>}
          {quality.missing_images > 0 && (
            <span className="text-yellow-600 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {quality.missing_images} no image
            </span>
          )}
          {quality.short_descriptions > 0 && <span className="text-yellow-600">{quality.short_descriptions} short desc</span>}
          {quality.out_of_stock > 0 && <span className="text-red-500">{quality.out_of_stock} out of stock</span>}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <select
          value={channelFilter}
          onChange={(e) => { setChannelFilter(e.target.value); updateState({ page: 1 }); }}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All Channels</option>
          {channels.map((ch) => (
            <option key={ch.id} value={ch.id}>{ch.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); updateState({ page: 1 }); }}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="error">Error</option>
          <option value="suppressed">Suppressed</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={listings}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        bulkActions={bulkActions}
        searchPlaceholder="Search by product name or SKU..."
        emptyState={
          <div className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No listings yet</h3>
            <p className="text-gray-600 mb-6">Create listings to publish products to your sales channels</p>
          </div>
        }
      />

      {/* Smart Suggest Modal */}
      {showSuggest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Smart Suggestions</h2>
                <p className="text-sm text-gray-500">{suggestions.length} product(s) ranked by listing readiness</p>
              </div>
              <button onClick={() => setShowSuggest(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {suggestions.length === 0 ? (
                <p className="text-center py-8 text-gray-500">All products are already listed on this channel.</p>
              ) : (
                <div className="space-y-3">
                  {suggestions.map((s) => (
                    <div key={s.product_id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-stone-50">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{s.product_name}</div>
                        <div className="text-sm text-gray-500">
                          ${s.base_price.toFixed(2)} &middot; {s.image_count} images &middot; {s.total_inventory} in stock
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`text-sm font-bold ${s.score >= 80 ? 'text-green-600' : s.score >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                          {s.score}/100
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleListSuggested([s.product_id])}
                        >
                          List
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {suggestions.length > 0 && (
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <Button variant="outline" size="md" onClick={() => setShowSuggest(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => handleListSuggested(suggestions.filter(s => s.score >= 60).map(s => s.product_id))}
                >
                  List All 60+ Score ({suggestions.filter(s => s.score >= 60).length})
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceListings;
